import React, { useState, useEffect, useRef } from 'react';
import { ZoomIn } from 'lucide-react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { tr } from 'date-fns/locale';

const MarkdownContent = ({ content, idToBlockMap, onZoom }) => {
    const processedContent = processContent(content, idToBlockMap, onZoom);
    
    return (
      <>
        {processedContent.map((part, index) => {
          if (part.type === 'react') {
            return React.cloneElement(part.content, { key: index });
          } else {
            return (
              <ReactMarkdown
                key={index}
                components={{
                  p: ({ node, ...props }) => <span {...props} />,
                  a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" />
                }}
              >
                {part.content}
              </ReactMarkdown>
            );
          }
        })}
      </>
    );
  };
  
  // Helper function to strip markdown
  const stripMarkdown = (text) => {
    return text
      .replace(/\*\*(.*?)\*\*/g, '$1') // Bold
      .replace(/\*(.*?)\*/g, '$1') // Italic
      .replace(/\[(.*?)\]\(.*?\)/g, '$1') // Links
      .replace(/\`(.*?)\`/g, '$1') // Inline code
      .replace(/\n+/g, ' ') // Replace newlines with spaces
      .replace(/^\s+|\s+$/g, ''); // Trim leading/trailing whitespace
  };
  
  const BlockRef = ({ id, idToBlockMap, onZoom }) => {
    const navigate = useNavigate();

    const block = idToBlockMap.get(id);
  
    if (!block) {
      return <span className="block-ref-error">Reference not found: {id}</span>;
    }
  
    const handleClick = (e) => {
      e.preventDefault();
      // onZoom(block.section || id);
      if (block.section){
        navigate(`/block/${block.section}#${id}`);
      }else{
        navigate(`/block/${id}`);
      }
      
    };
  
    const content = block.content || block['page-name'] || 'Unnamed Block';
    const plainTextContent = stripMarkdown(content);
    const truncatedContent = plainTextContent.length > 30 ? plainTextContent.substring(0, 30) + '...' : plainTextContent;
  
    return (
      <a href={`/block/${id}`} onClick={handleClick} className="block-ref">
        {truncatedContent}
      </a>
    );
  };
  
  const processContent = (content, idToBlockMap, onZoom) => {
    const regex = /\(\((.*?)\)\)/g;
    const parts = [];
    let lastIndex = 0;
    let match;
  
    while ((match = regex.exec(content)) !== null) {
      if (match.index > lastIndex) {
        parts.push({ type: 'text', content: content.slice(lastIndex, match.index) });
      }
      parts.push({
        type: 'react',
        content: (
          <BlockRef 
            key={match[1]} 
            id={match[1]} 
            idToBlockMap={idToBlockMap} 
            onZoom={onZoom} 
          />
        )
      });
      lastIndex = match.index + match[0].length;
    }
  
    if (lastIndex < content.length) {
      parts.push({ type: 'text', content: content.slice(lastIndex) });
    }
  
    return parts;
  };

  const defaultClosed = {
    'page': true,
    'section': true,
  }
  
  const OutlineNode = ({ block, idToBlockMap, idToReferencesMap, level = 0, onZoom }) => {
    const [isExpanded, setIsExpanded] = useState(level === 0 || block.properties?.collapsed !== 'false' && (!block.properties?.type || !defaultClosed[block.properties?.type]));
    const [isEmbedOpen, setIsEmbedOpen] = useState(false);
    const [isHighlighted, setIsHighlighted] = useState(false);
    const nodeRef = useRef(null);
    const location = useLocation();  

    const hasChildren = block.children && block.children.length > 0;
    const references = idToReferencesMap.get(block.id) || new Set();
  
    const toggleExpand = () => setIsExpanded(!isExpanded);
    const toggleEmbed = () => setIsEmbedOpen(!isEmbedOpen);

    useEffect(() => {
      if (location.hash === `#${block.id}` && nodeRef.current) {
        const scrollToElement = () => {
          nodeRef.current.scrollIntoView({ behavior: 'instant', block: 'center' });
          setIsHighlighted(true);
        setTimeout(() => setIsHighlighted(false), 2000); 
        };
  
        // Try scrolling immediately
        scrollToElement();
  
        // If that doesn't work, try again after a short delay
        setTimeout(scrollToElement, 100);
      }
    }, [location.hash, block.id]);
  
    const content = block.content || block['page-name'] || '';
  
    return (
      <li ref={nodeRef} id={block.id}>
        {hasChildren && (<span onClick={toggleExpand} className={`item ${isExpanded ? 'open' : 'closed'} type-${block.properties?.type}`}></span>)}
        {!hasChildren && (<span className="item"></span>)}
        <div className={`node-content ${isHighlighted ? 'highlighted' : ''}`}>
          {(block.properties?.type === 'page' || block.properties?.type === 'section') && level > 0 ? (
            <span onClick={() => onZoom(block.id)} className="page-link"><MarkdownContent content={content} idToBlockMap={idToBlockMap} onZoom={onZoom} /></span>
          ) : (
            <MarkdownContent content={content} idToBlockMap={idToBlockMap} onZoom={onZoom} />
          )}
          <ZoomIn
                size={14}
                className="zoom-icon"
                onClick={() => onZoom(block.id)}
                style={{ cursor: 'pointer', marginLeft: '5px' }}
              />
          {/* {Object.entries(block.properties).map(([key, value]) => (
            <div key={key} className="block-property">
              <span className="property-key">{key}:</span> {value}
            </div>
          ))} */}
          
          {references.size > 0 && (
            <span onClick={toggleEmbed} className="embedding-counter" data-counter={references.size}>
            </span>
          )}
        </div>
        {references.size > 0 && isEmbedOpen && (
          <div className={`embedded ${isEmbedOpen ? 'embed-open' : ''}`}>
            {Array.from(references).map((refId) => (
              <div key={refId} className="embedded-page">
                <OutlineNode 
                  key={idToBlockMap.get(refId).id} 
                  block={idToBlockMap.get(refId)} 
                  idToBlockMap={idToBlockMap}
                  idToReferencesMap={idToReferencesMap}
                  level={level + 1} 
                  onZoom={onZoom} 
                />
                {/* <MarkdownContent 
                  content={idToBlockMap.get(refId).content || ''} 
                  idToBlockMap={idToBlockMap} 
                  onZoom={onZoom} 
                /> */}
              </div>
            ))}
          </div>
        )}
        {hasChildren && (isExpanded || block.properties?.type === 'section') && (
          <div className="children">
            <ul>
              {block.children.map((child) => (
                (isExpanded || (block.properties?.type === 'section' && child.properties?.type === 'section')) && (
                <OutlineNode 
                  key={child.id} 
                  block={child} 
                  idToBlockMap={idToBlockMap}
                  idToReferencesMap={idToReferencesMap}
                  level={level + 1} 
                  onZoom={onZoom} 
                />) 
              ))}
            </ul>
          </div>
        )}
      </li>
    );
  };



export default OutlineNode;